import * as THREE from "three";
import { colors } from "../../utils/colors";
import { transformToModelSpace } from "../utils";

export const SPHERE_RADIUS = 0.15;
export const SNAP_HANDLE_RADIUS = 0.1;
export const SNAP_HANDLE_OFFSET = 0.05;
export const SNAP_POSITION_THRESHOLD = 0.1;
export const DEBOUNCE_TIME = 8;
export const SNAP_LINE_HANDLES_COLOR = colors.gray200();
export const DISTANCE_FROM_WINDOW_CENTER = 3;

export const applySensorOffset = (basePosition, offset, windowCoordSystem) => {
  if (!windowCoordSystem) return basePosition;

  const basePoint = new THREE.Vector3(...basePosition);
  const offsetZVector = windowCoordSystem.normal
    .clone()
    .multiplyScalar(offset?.z);

  const offsetYVector = windowCoordSystem.vAxis
    .clone()
    .multiplyScalar(0.01 * offset?.y * (windowCoordSystem?.vLength ?? 1));

  const offsetXVector = windowCoordSystem.uAxis
    .clone()
    .multiplyScalar(0.01 * offset?.x * (windowCoordSystem?.uLength ?? 1));

  return basePoint
    .add(offsetZVector)
    .add(offsetYVector)
    .add(offsetXVector)
    .toArray();
};

export const isTempSensor = (sensor) =>
  typeof sensor.id === "string" && sensor.id?.startsWith("TEMP_ID");

export const getMeshSensorDefaultColor = (sensor) => {
  return colors.blue500();
};

export const getMeshSensorColor = (
  sensorId,
  hoveredSensorId,
  selectedSensorsIds,
  isTempSensor
) => {
  return selectedSensorsIds?.includes(sensorId)
    ? colors.yellow500()
    : hoveredSensorId === sensorId
    ? colors.blue300()
    : isTempSensor
    ? colors.gray500()
    : getMeshSensorDefaultColor(sensorId);
};

export const getWindowLocalCoordSystem = (polygon) => {
  if (!polygon) return null;
  const parsedPolygon = Array.isArray(polygon) ? polygon : JSON.parse(polygon);
  const vertices = [];
  for (let i = 0; i < parsedPolygon.length; i += 3) {
    vertices.push(
      new THREE.Vector3(
        parsedPolygon[i],
        parsedPolygon[i + 1],
        parsedPolygon[i + 2]
      )
    );
  }

  if (vertices.length < 4) return null;

  const edge1 = vertices[1].clone().sub(vertices[0]);
  const edge2 = vertices[2].clone().sub(vertices[1]);

  const edge1Normalized = edge1.clone().normalize();
  const edge2Normalized = edge2.clone().normalize();
  const normal = edge1Normalized.clone().cross(edge2Normalized).normalize();

  const vAxis = normal.clone().cross(edge1Normalized).normalize();

  return {
    origin: vertices[0].clone(),
    uAxis: edge1Normalized,
    vAxis: vAxis,
    uLength: edge1.length(),
    vLength: edge2.length(),
    normal: normal,
  };
};

export const worldToWindowLocal = (worldPoint, windowCoordSystem) => {
  if (!windowCoordSystem) return { u: 0, v: 0 };

  const localPoint = worldPoint.clone().sub(windowCoordSystem.origin);
  return {
    u: localPoint.dot(windowCoordSystem.uAxis),
    v: localPoint.dot(windowCoordSystem.vAxis),
  };
};

export const windowLocalToWorld = (u, v, windowCoordSystem) => {
  if (!windowCoordSystem) return new THREE.Vector3();

  return windowCoordSystem.origin
    .clone()
    .add(windowCoordSystem.uAxis.clone().multiplyScalar(u))
    .add(windowCoordSystem.vAxis.clone().multiplyScalar(v));
};

const getMirrorSensor = (
  sensorPosition,
  originCoordSystem,
  targetWindow,
  targetCoordSystem
) => {
  const position3 = new THREE.Vector3(...sensorPosition);
  const localPosition = worldToWindowLocal(position3, originCoordSystem);
  const scaledU =
    (localPosition.u * targetCoordSystem.uLength) / originCoordSystem.uLength;
  const scaledV =
    (localPosition.v * targetCoordSystem.vLength) / originCoordSystem.vLength;

  const targetCoords = windowLocalToWorld(scaledU, scaledV, targetCoordSystem);
  const targetModelCoords = transformToModelSpace(targetCoords.toArray());

  const position = Array.isArray(targetModelCoords)
    ? targetModelCoords
    : targetModelCoords.toArray();

  const targetSensor = {
    id: "TEMP_ID" + Date.now() + Math.random(),
    position,
    windowId: targetWindow.id,
  };
  return targetSensor;
};

const getSingleMirrorWindowSensors = (
  originSensors,
  targetWindow,
  originCoordSystem,
  targetCoordSystem
) => {
  const targetSensors = originSensors
    ?.filter((sensor) => isTempSensor(sensor))
    .map((sensor) =>
      getMirrorSensor(
        sensor.position,
        originCoordSystem,
        targetWindow,
        targetCoordSystem
      )
    );
  return targetSensors;
};

export const getAllMirrorWindowSensors = (
  allWindows,
  originWindow,
  selectedWindowsIds,
  originSensors,
  originCoordSystem
) => {
  if (
    !originWindow ||
    !originSensors ||
    !originCoordSystem ||
    !allWindows ||
    !selectedWindowsIds
  )
    return [];

  const selectedMirrorWindows = allWindows?.filter(
    (window) =>
      window.id !== originWindow?.id && selectedWindowsIds?.includes(window.id)
  );
  return selectedMirrorWindows?.map((window) => {
    const polygon = window.polygon;
    const targetCoordSystem = getWindowLocalCoordSystem(polygon);

    const mirrorSensors = getSingleMirrorWindowSensors(
      originSensors,
      window,
      originCoordSystem,
      targetCoordSystem
    );
    return {
      sensors: mirrorSensors,
      windowCoordSystem: targetCoordSystem,
    };
  });
};

export const getBaseWindow = (selectedObjects) => {
  return selectedObjects?.[0];
};

export const getWindowPolygonYUp = (window) => {
  const originalPolygon =
    typeof window?.polygon === "string"
      ? JSON.parse(window?.polygon)
      : window?.polygon;

  const yUpPolygon = [];
  for (let i = 0; i < originalPolygon?.length; i += 3) {
    yUpPolygon.push(
      originalPolygon[i],
      originalPolygon[i + 2],
      -originalPolygon[i + 1]
    );
  }

  const transformedWindow = {
    ...window,
    polygon: yUpPolygon,
  };
  return transformedWindow;
};

export const getWindowCenter = (polygon) => {
  const vertices = [];
  for (let i = 0; i < polygon.length; i += 3) {
    vertices.push(
      new THREE.Vector3(polygon[i], polygon[i + 1], polygon[i + 2])
    );
  }

  const center = new THREE.Vector3();
  vertices.forEach((v) => center.add(v));
  center.divideScalar(vertices.length);
  return center;
};

export const getWindowNormal = (polygon) => {
  const vertices = [];
  for (let i = 0; i < polygon.length; i += 3) {
    vertices.push(
      new THREE.Vector3(polygon[i], polygon[i + 1], polygon[i + 2])
    );
  }

  if (vertices.length < 3) return new THREE.Vector3(0, 0, 1);

  const v1 = vertices[1].clone().sub(vertices[0]);
  const v2 = vertices[2].clone().sub(vertices[0]);
  return v1.cross(v2).normalize();
};

export const getSnapPosition = (
  worldPosition,
  snapLines,
  coordSystem,
  threshold = SNAP_POSITION_THRESHOLD
) => {
  if (!coordSystem || !snapLines.horizontal || !snapLines.vertical) {
    return { position: worldPosition, snapped: false };
  }

  const worldPoint = new THREE.Vector3(...worldPosition);
  const localCoords = worldToWindowLocal(worldPoint, coordSystem);
  let snappedU = localCoords.u;
  let snappedV = localCoords.v;
  let snapped = false;

  snapLines.horizontal.forEach((line) => {
    if (line.v !== undefined && Math.abs(localCoords.v - line.v) < threshold) {
      snappedV = line.v;
      snapped = true;
    }
  });

  snapLines.vertical.forEach((line) => {
    if (line.u !== undefined && Math.abs(localCoords.u - line.u) < threshold) {
      snappedU = line.u;
      snapped = true;
    }
  });

  const snappedWorldPos = windowLocalToWorld(snappedU, snappedV, coordSystem);
  return {
    position: snappedWorldPos.toArray(),
    snapped,
    localCoords: { u: snappedU, v: snappedV },
  };
};

export const calculateSnapLines = (windowPolygon, sensors, coordSystem) => {
  const parsedWindowPolygon =
    typeof windowPolygon === "string"
      ? JSON.parse(windowPolygon)
      : windowPolygon;

  if (!parsedWindowPolygon || !coordSystem) {
    return { horizontal: [], vertical: [] };
  }

  const horizontal = [];
  const vertical = [];

  sensors?.forEach((sphere) => {
    const spherePoint = new THREE.Vector3(...sphere.position);
    const localCoords = worldToWindowLocal(spherePoint, coordSystem);

    horizontal.push({
      v: localCoords.v,
      localCoords: localCoords,
    });

    vertical.push({
      u: localCoords.u,
      localCoords: localCoords,
    });
  });

  const vertices = [];
  for (let i = 0; i < parsedWindowPolygon.length; i += 3) {
    const vertex = new THREE.Vector3(
      parsedWindowPolygon[i],
      parsedWindowPolygon[i + 1],
      parsedWindowPolygon[i + 2]
    );
    const localCoords = worldToWindowLocal(vertex, coordSystem);
    vertices.push(localCoords);
  }

  const minU = Math.min(...vertices.map((v) => v.u));
  const maxU = Math.max(...vertices.map((v) => v.u));
  const minV = Math.min(...vertices.map((v) => v.v));
  const maxV = Math.max(...vertices.map((v) => v.v));

  horizontal.push({ v: minV }, { v: maxV });
  vertical.push({ u: minU }, { u: maxU });

  return { horizontal, vertical, bounds: { minU, maxU, minV, maxV } };
};

export const isSnapTooClose = (newValue, oldValue, lineType, snapLines) => {
  return snapLines[lineType].some((line) => {
    const lineValue =
      lineType === "horizontal" ? line.localCoords?.v : line.localCoords?.u;
    return (
      oldValue !== newValue &&
      Math.abs(lineValue - newValue) < SNAP_POSITION_THRESHOLD
    );
  });
};
